import './personal-info.scss'
import 'react-day-picker/src/style.css'
import 'dayjs/locale/th'

import dayjs from 'dayjs'
import buddhistEra from 'dayjs/plugin/buddhistEra'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { DayPicker, Modifiers } from 'react-day-picker'
import { th } from 'react-day-picker/locale'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import Header from '../../components/collections/Header'
import { CoverSpinning } from '../../components/elements'
import Modal from '../../components/modals/Modal'
import TagManager from '../../lib/tag-manager'
import { useAppState } from '../../providers/store'
import getAPI from '../../services'

dayjs.extend(buddhistEra)

export interface PersonalInfoForm {
    postponeDate: string
    postponeValue: Date
    postponeReasonAny: string
    commitToPay?: number
    tel: string
    postponeReason: string
    emergTel: string
}

interface NoticeModalProps {
    title: string
    message: string
    toggle(e: React.SyntheticEvent<HTMLButtonElement>): void
}
function NoticeModal({ title, message, toggle }: NoticeModalProps) {
    return (
        <Modal>
            <div className="notice-modal-content abcs-container">
                <header>
                    <h4>{title}</h4>
                    <p>{message}</p>
                </header>

                <button type="button" className="abcs-button fluid primary" onClick={toggle}>
                    รับทราบ
                </button>
            </div>
        </Modal>
    )
}

function PersonalInfo() {
    const [loading, setLoading] = useState(false)
    const [submittedForm, setSubmittedForm] = useState(false)
    const [noticeModal, setNoticeModal] = useState<null | { title: string; message: string }>(null)
    const [popupDatePicker, setPopupDatePicker] = useState(false)
    const [dpd, setDpd] = useState<number | null>(null)
    const appState = useAppState()
    const navigate = useNavigate()
    const {
        register,
        setValue,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm<PersonalInfoForm>()
    const onToggleDatePicker = () => {
        setPopupDatePicker(!popupDatePicker)
    }

    const onDayClick = (day: Date, modifiers: Modifiers) => {
        if (modifiers.disabled) return
        setValue('postponeDate', dayjs(day).locale('th').format('DD MMMM BBBB'), {
            shouldValidate: true,
        })
        setValue('postponeValue', day, { shouldValidate: true })
        setPopupDatePicker(false)
    }

    const onSubmitFormData = async (data: PersonalInfoForm) => {
        const payload = window.liff.getDecodedIDToken()
        let userId: string
        if (payload?.sub) {
            userId = payload.sub
        } else {
            const profile = await window.liff.getProfile()
            userId = profile.userId
        }

        try {
            setLoading(true)
            const note = `${
                data.postponeReason === 'other' ? data.postponeReasonAny : data.postponeReason
            } (เบอร์ติดต่อฉุกเฉิน ${data.emergTel})`
            const result = await getAPI().ptpService.submitForm(
                {
                    note,
                    partyId: appState.user?.partyId,
                    pinUsername: appState.user?.pinUsername,
                    contactNumber: data.tel,
                    ptpDate: data.postponeValue,
                    ptpAmount: Number(data.commitToPay),
                },
                userId
            )
            TagManager.popupEvent({
                action: 'popup-success',
                field: JSON.stringify(result),
                customer_id: userId,
                page: 'promise-to-pay-personal-info',
            })
            setSubmittedForm(true)
        } catch (error) {
            setLoading(false)
            setNoticeModal({ title: 'เกิดข้อผิดพลาด', message: error.message })
            TagManager.popupEvent({
                action: 'popup-error',
                field: JSON.stringify(error),
                customer_id: userId,
                page: 'promise-to-pay-personal-info',
            })
        } finally {
            setLoading(false)
        }
    }

    const getCustomerDpd = useCallback(async () => {
        if (!appState?.user?.pinUsername) {
            setNoticeModal({
                title: 'เกิดข้อผิดพลาด',
                message: 'ไม่พบข้อมูล',
            })
            return
        }

        try {
            setLoading(true)
            const result = await getAPI(undefined, 'v2').ptpService.getCustomerDpd(
                appState?.user?.pinUsername
            )
            setDpd(result.dpd)
        } catch (error) {
            setNoticeModal({ title: 'เกิดข้อผิดพลาด', message: error.message })
        } finally {
            setLoading(false)
        }
    }, [appState?.user?.pinUsername])

    const getMaxPtpDay = () => {
        const isDpdLessThanSixtyOne = (dpd ?? 0) < 61
        return isDpdLessThanSixtyOne ? 3 : 7
    }

    const isOtherReason = watch('postponeReason') === 'other'

    useEffect(() => {
        getCustomerDpd()
    }, [getCustomerDpd])

    useEffect(() => {
        register('postponeDate')
        register('postponeValue')
    }, [register])

    useEffect(() => {
        if (submittedForm) {
            navigate('/promise-to-pay/done')
        }
        return () => setSubmittedForm(false)
    }, [navigate, submittedForm])

    useMemo(() => {
        TagManager.loadEvent({ page: 'promise-to-pay-personal-info' })
    }, [])

    return (
        <>
            {loading && <CoverSpinning />}
            {noticeModal && (
                <NoticeModal
                    title={noticeModal.title}
                    message={noticeModal.message}
                    toggle={() => setNoticeModal(null)}
                />
            )}
            {popupDatePicker && (
                <Modal toggleModal={onToggleDatePicker}>
                    <DayPicker
                        showOutsideDays
                        locale={th}
                        onDayClick={onDayClick}
                        month={new Date()}
                        disabled={[
                            { before: new Date() },
                            {
                                after: new Date(
                                    new Date().setDate(new Date().getDate() + getMaxPtpDay())
                                ),
                            },
                        ]}
                        modifiers={{
                            after: new Date(2020, 5, 20),
                            before: new Date(2020, 5, 30),
                        }}
                        modifiersStyles={{
                            today: {
                                color: 'white',
                                backgroundColor: '#1b2b3e',
                                borderRadius: '100%',
                            },
                        }}
                    />
                </Modal>
            )}
            <div className="personal-info">
                <Header
                    title="การชำระค่างวด"
                    messages={['ยังทำรายการไม่เสร็จ กรุณาทำรายการใหม่อีกครั้ง']}
                />
                <div className="abcs-container gap-y-4 py-4">
                    <h6>ข้อมูลสำหรับการติดต่อ สอบถาม</h6>

                    <div className="postpone-reason-container">
                        <div>
                            สาเหตุที่ต้องการเลื่อนนัด
                            <span style={{ color: 'red' }}>*</span>
                        </div>

                        <div
                            className={`flex flex-row justify-start ${
                                errors.postponeReason && 'error'
                            }`}
                        >
                            <input
                                id="postponeReason_not-matched"
                                type="radio"
                                value="วันเงินเดือนออกไม่ตรงกับวันกำหนดชำระ"
                                {...register('postponeReason', { required: 'กรุณาเลือก' })}
                            />
                            <label
                                htmlFor="postponeReason_not-matched"
                                className="self-center ml-2"
                            >
                                <span className="block">วันเงินเดือนออกไม่ตรงกับวันกำหนดชำระ</span>
                                <small>กำหนดชำระทุกวันที่ 28 ของทุกเดือน</small>
                            </label>
                        </div>

                        <div
                            className={`flex flex-row justify-start ${
                                errors.postponeReason && 'error'
                            }`}
                        >
                            <input
                                id="postponeReason_other"
                                value="other"
                                type="radio"
                                {...register('postponeReason', { required: 'กรุณาเลือก' })}
                            />
                            <label htmlFor="postponeReason_other" className="self-center ml-2">
                                สาเหตุอื่นๆ (โปรดระบุ)
                            </label>
                        </div>

                        {isOtherReason && (
                            <div
                                className={`abcs-input fluid ${
                                    errors.postponeReasonAny && 'error'
                                }`}
                            >
                                <input
                                    type="text"
                                    maxLength={200}
                                    {...register('postponeReasonAny', { required: 'กรุณาระบุ' })}
                                />
                                {errors.postponeReasonAny && (
                                    <small>{errors.postponeReasonAny.message}</small>
                                )}
                            </div>
                        )}

                        {errors.postponeReason && (
                            <small className="text-red-500">{errors.postponeReason.message}</small>
                        )}
                    </div>
                    <div className={`abcs-input fluid ${errors.postponeDate && 'error'}`}>
                        <label htmlFor="postponeDate">
                            วันที่ต้องการเลื่อนชำระ<span style={{ color: 'red' }}>*</span>
                        </label>
                        <input
                            readOnly
                            id="postponeDate"
                            type="text"
                            onClick={onToggleDatePicker}
                            {...register('postponeDate', { required: 'กรุณาระบุวันที่' })}
                        />
                        {errors.postponeDate && <small>{errors.postponeDate.message}</small>}
                    </div>
                    <div className={`abcs-input fluid ${errors.commitToPay && 'error'}`}>
                        <label htmlFor="commitToPay">
                            จำนวนเงินที่คาดว่าจะชำระได้(ระบุเป็นตัวเลข)
                        </label>
                        <input
                            id="commitToPay"
                            pattern="[0-9]*"
                            type="text"
                            inputMode="numeric"
                            {...register('commitToPay', {
                                pattern: {
                                    value: /^[0-9]*$/,
                                    message: 'กรุณาระบุเป็นตัวเลข',
                                },
                            })}
                        />
                        {errors.commitToPay?.type === 'pattern' && (
                            <small>{errors.commitToPay.message}</small>
                        )}
                    </div>
                    <div className={`abcs-input fluid ${errors.tel && 'error'}`}>
                        <label htmlFor="tel">
                            เบอร์โทรศัพท์<span style={{ color: 'red' }}>*</span>
                        </label>
                        <input
                            id="tel"
                            maxLength={10}
                            placeholder="0808888888"
                            pattern="[0-9]*"
                            type="text"
                            inputMode="numeric"
                            {...register('tel', {
                                pattern: {
                                    // eslint-disable-next-line no-useless-escape
                                    value: /((\+66|0)(\d{1,2}\-?\d{3}\-?\d{3,4}))/,
                                    message: 'กรุณาระบุเป็นหมายเลขโทรศัพท์',
                                },
                                required: 'กรุณากรอกเบอร์โทรศัพท์ 10 หลัก',
                            })}
                        />

                        {errors.tel?.type === 'pattern' && <small>{errors.tel.message}</small>}
                        {errors.tel?.type === 'required' && <small>{errors.tel.message}</small>}
                    </div>
                    <div className={`abcs-input fluid ${errors.emergTel && 'error'}`}>
                        <label htmlFor="emergTel">
                            เบอร์โทรศัพท์ผู้ติดต่อฉุกเฉิน<span style={{ color: 'red' }}>*</span>
                        </label>
                        <input
                            id="emergTel"
                            maxLength={10}
                            placeholder="0808888888"
                            pattern="[0-9]*"
                            type="text"
                            inputMode="numeric"
                            {...register('emergTel', {
                                pattern: {
                                    // eslint-disable-next-line no-useless-escape
                                    value: /((\+66|0)(\d{1,2}\-?\d{3}\-?\d{3,4}))/,
                                    message: 'กรุณาระบุเป็นหมายเลขโทรศัพท์',
                                },
                                required: 'กรุณากรอกเบอร์โทรศัพท์ 10 หลัก',
                            })}
                        />

                        {errors.emergTel?.type === 'pattern' && (
                            <small>{errors.emergTel.message}</small>
                        )}
                        {errors.emergTel?.type === 'required' && (
                            <small>{errors.emergTel.message}</small>
                        )}
                    </div>
                    <footer className="footer">
                        <button
                            className={`abcs-button primary  fluid `}
                            type="submit"
                            onClick={handleSubmit(onSubmitFormData)}
                        >
                            บันทึก
                        </button>
                    </footer>
                </div>
            </div>
        </>
    )
}

export default PersonalInfo
